import {
  Document,
  Page,
  Text,
  View,
  Font,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import {
  convertAmountToWords,
  formatAmount,
  stringToUpperCase,
} from "../../../components/Helper";
import { CURRENCY } from "../../../components/Constants";

Font.register({
  family: "HostGrotesk",
  fonts: [
    { src: "/fonts/HostGrotesk-Regular.ttf" },
    { src: "/fonts/HostGrotesk-Bold.ttf", fontWeight: "bold" },
  ],
});

export const calculateMultipleKeys = (
  items: any,
  keyNames: string[],
): { [key: string]: number } => {
  const result: { [key: string]: number } = {};

  keyNames.forEach((key) => {
    result[key] = 0;
  });

  items.forEach((item: any) => {
    keyNames.forEach((key) => {
      result[key] += Number(item[key]) || 0;
    });
  });

  return result;
};

export const styles = StyleSheet.create({
  boldText: {
    fontWeight: "bold",
    fontSize: 6.8,
  },
  borderBottom: {
    borderBottom: 1,
  },
  smallText: {
    fontSize: 6,
  },
  mediumText: {
    fontSize: 6.5,
  },
  contentPadding: {
    padding: 1,
  },
  page: {
    paddingHorizontal: 30,
    // paddingVertical: 15,
    paddingTop: 85,
    paddingBottom: 143,
    fontFamily: "HostGrotesk",
    backgroundColor: "white",
  },
  header: {
    position: "absolute",
    top: -70,
    left: 0,
    right: 0,
    height: 200,
    alignItems: "center",
    fontSize: 12,
    padding: 5,
  },
  headerImage: {
    width: "90%",
    height: "100%",
  },
  body: {
    // top: 65,
    // maxHeight: 615,
  },
  footer: {
    position: "absolute",
    bottom: 25,
    left: 0,
    right: 0,
    paddingHorizontal: 40,
    height: 120,
    fontSize: 8,
    marginBottom: 1.5,
    marginTop: 1.5,
  },
  logo: {
    height: "60px",
    width: "60px",
  },
  footerInline: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "center",
  },
  subheader: {
    display: "flex",
    fontSize: 10,
    textAlign: "center",
    alignItems: "center",
    padding: 1,
    marginBottom: 0,
    fontWeight: "bold",
  },
  tableContainer: {
    borderTop: 1,
    borderLeft: 1,
    marginVertical: 8,
    marginHorizontal: 2,
    // width: "100%",
  },
  tableRow: {
    borderBottom: 1,
    flexDirection: "row",
    width: "100%",
    fontSize: 6.9,
    alignItems: "stretch",
  },
  tableCell: {
    padding: 1,
    borderRight: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    flexGrow: 1,
    flexBasis: "auto",
  },
  section: {
    margin: 2,
    borderTop: 1,
    borderLeft: 1,
  },
  flex: {
    display: "flex",
    flexDirection: "row",
  },
  flexContent: {
    borderRight: 1,
    borderBottom: 1,
    width: "100%",
    fontSize: 8,
  },
  textBlock: { fontSize: 6.7, paddingBottom: 1.5, paddingHorizontal: 3 },
  subComponentHeading: {
    paddingHorizontal: 3,
    fontSize: 8,
    // marginBottom: 1.5,
    borderBottom: 1,
  },
  containerDetailText: {
    fontSize: 6.5,
    marginTop: 1,
  },
});

const SalesContractInvoice = ({
  settingData,
  buyerData,
  salesContractData,
  companyData,
}: {
  settingData: any;
  buyerData: any;
  salesContractData: any;
  companyData: any;
}) => {
  const { quantity: grandTotalQuantity, total_amount: grandTotalAmount } =
    calculateMultipleKeys(salesContractData.sales_contract_product, [
      "quantity",
      "total_amount",
    ]);

  const qualitySpecifications = salesContractData.quality_specifications || [];
  const middleIndex = Math.ceil(qualitySpecifications.length / 2);

  const qsLeftData = qualitySpecifications.slice(0, middleIndex);
  const qsRightData = qualitySpecifications.slice(middleIndex);

  const documents = salesContractData.documents || [];
  const documentMiddleIndex = Math.ceil(documents.length / 2);
  const leftSideDocumentData = documents.slice(0, documentMiddleIndex);
  const rightSideDocumentData = documents.slice(documentMiddleIndex);

  const borderTop = {
    bankDetails: { borderTop: documents?.length > 0 ? 0 : 1 },
    generalTerms: {
      borderTop:
        documents?.length > 0 ||
        salesContractData?.exporter_bank ||
        salesContractData?.buyer_bank
          ? 0
          : 1,
    },
    weightAndQuality: {
      borderTop:
        documents?.length > 0 ||
        salesContractData?.exporter_bank ||
        salesContractData?.buyer_bank ||
        salesContractData.general_terms?.length > 0
          ? 0
          : 1,
    },
  };

  const logoSignatureCss =
    companyData.name === "ENFINY EXIM LLP"
      ? {
          height: "60px",
          width: "120px",
        }
      : styles.logo;

  return (
    <Document>
      <Page style={styles.page} size="A4">
        {/* Header  */}
        <View style={styles.header} fixed>
          {companyData?.invoice_header && (
            <Image
              src={`${process.env.REACT_APP_BACKEND_URL}${companyData?.invoice_header}`}
              style={[
                {
                  width:
                    companyData?.invoice_header_width ||
                    styles.headerImage.width,
                  height:
                    companyData?.invoice_header_height ||
                    styles.headerImage.height,
                },
              ]}
            />
          )}
        </View>

        <View style={[styles.body]}>
          <View style={[styles.subheader]}>
            <Text style={[styles.borderBottom]}>SALES CONTRACT</Text>
          </View>

          {/* Shipper/Exporter & Buyer/Consignee Details */}
          <View style={styles.section}>
            <View style={styles.flex}>
              <View style={[styles.flexContent]}>
                <Text style={styles.subComponentHeading}>
                  <Text style={styles.boldText}>DATE : </Text>
                  {moment(salesContractData?.contract_date).format(
                    "DD/MM/YYYY",
                  )}
                </Text>
                <Text
                  style={[
                    styles.borderBottom,
                    styles.textBlock,
                    {
                      width: "33%",
                    },
                  ]}
                >
                  SHIPPER EXPORTER :
                </Text>
                <Text
                  style={[
                    styles.boldText,
                    { fontSize: 7.5, paddingHorizontal: 3 },
                  ]}
                >
                  {stringToUpperCase(settingData.company_name)}
                </Text>

                <Text style={styles.textBlock}>
                  {stringToUpperCase(settingData.company_address)}
                </Text>
              </View>

              <View style={[styles.flexContent]}>
                <Text style={styles.subComponentHeading}>
                  <Text style={styles.boldText}>CONTRACT NO :</Text>{" "}
                  {stringToUpperCase(salesContractData?.contract_no)}{" "}
                  {salesContractData?.reference_no
                    ? `(${salesContractData?.reference_no})`
                    : ""}
                </Text>
                <Text
                  style={[
                    styles.borderBottom,
                    styles.textBlock,
                    {
                      width: "35%",
                    },
                  ]}
                >
                  BUYER / CONSIGNEE :
                </Text>
                <Text
                  style={[
                    styles.boldText,
                    { fontSize: 7.2, paddingHorizontal: 3 },
                  ]}
                >
                  {stringToUpperCase(buyerData.name)}
                </Text>
                <Text style={styles.textBlock}>
                  {stringToUpperCase(buyerData.address)}
                </Text>
              </View>
            </View>
          </View>

          {/* Particulars & Shipment Term */}
          <View style={styles.section}>
            <View style={styles.flex}>
              <View style={[styles.flexContent]}>
                <Text style={styles.subComponentHeading}>
                  <Text style={styles.boldText}>PRODUCTS : </Text>
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>QUANTITY : </Text>
                  {formatAmount(grandTotalQuantity, 3)} MTS
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>VALUE : </Text>
                  {CURRENCY.USD.NAME} {formatAmount(grandTotalAmount)}
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>VALUE IN WORDS : </Text>
                  {convertAmountToWords(
                    Number(grandTotalAmount),
                    CURRENCY.USD.NAME,
                  )}
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>MARKING : </Text>
                  {stringToUpperCase(salesContractData?.marking)}
                </Text>
              </View>

              <View style={[styles.flexContent]}>
                <Text style={styles.subComponentHeading}>
                  <Text style={styles.boldText}>SHIPMENT TERM : </Text>
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>FINAL DESTINATION : </Text>
                  {stringToUpperCase(salesContractData?.final_destination)}
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>PORT OF LOADING : </Text>
                  {stringToUpperCase(salesContractData?.port_of_loading?.name)}
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>PORT OF DISCHARGE : </Text>
                  {stringToUpperCase(salesContractData?.port_of_discharge)}
                </Text>
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>SHIPMENT : </Text>
                  {stringToUpperCase(salesContractData?.shipment)}
                </Text>
                {salesContractData.trans_shipment && (
                  <Text style={styles.textBlock}>
                    <Text style={styles.boldText}>TRANS SHIPMENT : </Text>
                    {stringToUpperCase(salesContractData?.trans_shipment)}
                  </Text>
                )}
                {salesContractData.partial_shipment && (
                  <Text style={styles.textBlock}>
                    <Text style={styles.boldText}>PARTIAL SHIPMENT : </Text>
                    {stringToUpperCase(salesContractData?.partial_shipment)}
                  </Text>
                )}
                <Text style={styles.textBlock}>
                  <Text style={styles.boldText}>PAYMENT TERMS : </Text>
                  {stringToUpperCase(salesContractData?.payment_terms)}
                </Text>
              </View>
            </View>
          </View>

          {/* Products Information */}
          {salesContractData?.sales_contract_product?.length && (
            <View
              style={[
                styles.tableContainer,
                // styles.mediumFontSize,
                { borderLeft: 0 },
              ]}
              wrap
            >
              <View
                style={[
                  styles.tableRow,
                  styles.boldText,
                  { borderLeft: 1, height: 15 },
                ]}
              >
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: "6%",
                    },
                  ]}
                >
                  SR NO
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: "22%",
                    },
                  ]}
                >
                  COMMODITY
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.boldText,
                    {
                      width: "18%",
                    },
                  ]}
                >
                  PACKING
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: "18%",
                    },
                  ]}
                >
                  QUANTITY (MTS)
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.boldText,
                    {
                      width: "18%",
                    },
                  ]}
                >
                  PRICE
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.boldText,
                    {
                      width: "18%",
                    },
                  ]}
                >
                  VALUE
                </Text>
              </View>

              {salesContractData?.sales_contract_product?.map(
                (row: any, index: number) => {
                  return (
                    <View
                      style={[
                        styles.tableRow,
                        // styles.mediumFontSize,
                        { borderLeft: 1 },
                      ]}
                      key={index}
                      wrap={false}
                    >
                      <Text
                        style={[
                          styles.tableCell,
                          styles.boldText,
                          {
                            width: "6%",
                          },
                        ]}
                      >
                        {index + 1}
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          styles.boldText,
                          {
                            width: "22%",
                            fontSize: 7,
                          },
                        ]}
                      >
                        {stringToUpperCase(row.products?.name)}
                        {row.products?.variety
                          ? ` - ${row.products?.variety}`
                          : ""}
                        {row.products?.hsn_no && ` (${row.products?.hsn_no})`}
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          { fontSize: 6.5, width: "18%" },
                        ]}
                      >
                        {`${
                          row?.packing_net_weight
                            ? ` NET ${formatAmount(
                                row?.packing_net_weight,
                                3,
                              )} KGS`
                            : ""
                        }${
                          row?.packing_gross_weight
                            ? ` & GROSS ${formatAmount(
                                row?.packing_gross_weight,
                                3,
                              )} KGS`
                            : ""
                        } ${
                          row?.packing_type?.name
                            ? `${stringToUpperCase(row?.packing_type?.name)}`
                            : "BAGS"
                        }`}
                      </Text>
                      <View
                        style={[
                          styles.tableCell,
                          {
                            width: "18%",
                            flexDirection: "column",
                            alignItems: "center",
                          },
                        ]}
                      >
                        <Text>{formatAmount(row?.quantity, 3)}</Text>

                        {(row?.no_of_containers || row?.container_load) && (
                          <Text style={styles.containerDetailText}>
                            (+/- 5%){" "}
                            {row?.no_of_containers
                              ? `${row.no_of_containers} FCL `
                              : ""}
                            {row?.container_load
                              ? `${row.container_load} FEET`
                              : ""}
                          </Text>
                        )}
                      </View>
                      <Text
                        style={[
                          styles.tableCell,
                          {
                            width: "18%",
                          },
                        ]}
                      >
                        {CURRENCY.USD.NAME} {formatAmount(row?.price)}
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          {
                            width: "18%",
                          },
                        ]}
                      >
                        {CURRENCY.USD.NAME} {formatAmount(row?.total_amount)}
                      </Text>
                    </View>
                  );
                },
              )}
            </View>
          )}

          {/* Quality Specifications */}
          {salesContractData.quality_specifications.length > 0 && (
            <View style={styles.section} wrap={false}>
              <Text
                style={[styles.flexContent, styles.textBlock, styles.boldText]}
              >
                QUALITY SPECIFICATION :
              </Text>
              <View style={styles.flex}>
                <View style={[styles.flexContent, { width: "50%" }]}>
                  {qsLeftData.map((element: any, index: number) => {
                    return (
                      <Text style={styles.textBlock} key={index}>
                        <Text style={styles.boldText}>
                          {index + 1}.{" "}
                          {stringToUpperCase(
                            element?.quality_specification?.name,
                          )}{" "}
                          :
                        </Text>{" "}
                        {stringToUpperCase(element?.value)}
                      </Text>
                    );
                  })}
                </View>
                <View style={[styles.flexContent, { width: "50%" }]}>
                  {qsRightData &&
                    qsRightData.map((element: any, index: number) => {
                      return (
                        <Text style={styles.textBlock} key={index}>
                          <Text style={styles.boldText}>
                            {index + 1 + middleIndex}.{" "}
                            {stringToUpperCase(
                              element?.quality_specification?.name,
                            )}{" "}
                            :
                          </Text>{" "}
                          {stringToUpperCase(element?.value)}
                        </Text>
                      );
                    })}
                </View>
              </View>
            </View>
          )}

          {/* Documents */}
          {salesContractData.documents.length > 0 && (
            <View style={[styles.section, { marginBottom: 0 }]} wrap={false}>
              <Text
                style={[styles.flexContent, styles.textBlock, styles.boldText]}
              >
                REQUIRED DOCUMENTS :
              </Text>
              <View style={styles.flex}>
                <View style={[styles.flexContent, { width: "50%" }]}>
                  {leftSideDocumentData &&
                    leftSideDocumentData.map((element: any, index: number) => {
                      return (
                        <Text style={styles.textBlock} key={index}>
                          <Text style={styles.boldText}>
                            {index + 1}.{" "}
                            {stringToUpperCase(element?.document?.name)} :
                          </Text>{" "}
                          {stringToUpperCase(element?.value)}
                        </Text>
                      );
                    })}
                </View>
                <View style={[styles.flexContent, { width: "50%" }]}>
                  {rightSideDocumentData &&
                    rightSideDocumentData.map((element: any, index: number) => {
                      return (
                        <Text style={styles.textBlock} key={index}>
                          <Text style={styles.boldText}>
                            {index + 1 + documentMiddleIndex}.{" "}
                            {stringToUpperCase(element?.document?.name)} :
                          </Text>{" "}
                          {stringToUpperCase(element?.value)}
                        </Text>
                      );
                    })}
                </View>
              </View>
            </View>
          )}

          {/* Bank Details */}
          {(salesContractData?.exporter_bank ||
            salesContractData?.buyer_bank) && (
            <View
              style={[
                styles.section,
                styles.flex,
                { ...borderTop.bankDetails, marginVertical: 0 },
              ]}
              wrap={false}
            >
              {salesContractData?.exporter_bank && (
                <View style={[styles.flexContent]}>
                  <Text
                    style={[
                      styles.textBlock,
                      styles.boldText,
                      { borderBottom: 1 },
                    ]}
                  >
                    EXPORTER BANK DETAILS :
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>BANK NAME :</Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.bank_name,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>OUR A/C NO : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.account_number,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>IFSC CODE : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.ifsc_code,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>BRANCH NAME : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.branch_name,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>SWIFT CODE : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.swift_code,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>AD CODE : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.ad_code,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>ADDRESS : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.exporter_bank?.address,
                    )}
                  </Text>
                </View>
              )}

              {salesContractData?.buyer_bank && (
                <View style={[styles.flexContent]}>
                  <Text
                    style={[
                      styles.textBlock,
                      styles.boldText,
                      { borderBottom: 1 },
                    ]}
                  >
                    BUYER BANK DETAILS :
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>BANK NAME :</Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.buyer_bank?.bank_name,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>OUR A/C NO : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.buyer_bank?.account_number,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>IFSC CODE : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.buyer_bank?.ifsc_code,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>BRANCH NAME : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.buyer_bank?.branch_name,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>SWIFT CODE : </Text>{" "}
                    {stringToUpperCase(
                      salesContractData?.buyer_bank?.swift_code,
                    )}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>AD CODE : </Text>{" "}
                    {stringToUpperCase(salesContractData?.buyer_bank?.ad_code)}
                  </Text>
                  <Text style={styles.textBlock}>
                    <Text>ADDRESS : </Text>{" "}
                    {stringToUpperCase(salesContractData?.buyer_bank?.address)}
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* General Terms */}
          {salesContractData.general_terms?.length > 0 && (
            <View
              style={[
                styles.section,
                { ...borderTop.generalTerms, marginVertical: 0 },
              ]}
              wrap={false}
            >
              <View style={[styles.flexContent, styles.contentPadding]}>
                <Text
                  style={[
                    styles.boldText,
                    styles.textBlock,
                    { borderBottom: 1 },
                  ]}
                >
                  GENERAL TERMS :
                </Text>
                {salesContractData.general_terms.map(
                  (item: any, index: number) => (
                    <View
                      style={[styles.flex, { alignItems: "center" }]}
                      key={index}
                    >
                      <Text
                        style={
                          (styles.boldText, { fontSize: 10, paddingRight: 2 })
                        }
                      >
                        {" "}
                        •
                      </Text>
                      <Text>{item.general_terms.term}</Text>
                    </View>
                  ),
                )}
              </View>
            </View>
          )}

          {/* Weight And Quality */}
          <View
            style={[
              styles.section,
              { ...borderTop.weightAndQuality, marginVertical: 0 },
            ]}
            wrap={false}
          >
            <View style={[styles.flexContent, styles.contentPadding]}>
              {salesContractData?.weight_and_quality && (
                <>
                  <Text
                    style={[
                      styles.borderBottom,
                      styles.textBlock,
                      styles.boldText,
                      {
                        width: "18%",
                      },
                    ]}
                  >
                    WEIGHT AND QUALITY :
                  </Text>
                  <Text style={styles.textBlock}>
                    {salesContractData?.weight_and_quality}
                  </Text>
                </>
              )}
              {salesContractData?.arbitration && (
                <>
                  <Text
                    style={[
                      styles.borderBottom,
                      styles.textBlock,
                      styles.boldText,
                      {
                        width: "12%",
                      },
                    ]}
                  >
                    ARBITRATION :
                  </Text>
                  <Text style={styles.textBlock}>
                    {salesContractData?.arbitration}
                  </Text>
                </>
              )}
              <Text
                style={[
                  styles.borderBottom,
                  styles.textBlock,
                  styles.boldText,
                  styles.contentPadding,
                  {
                    width: "40%",
                  },
                ]}
              >
                LANGUAGE IN WHICH DOCUMENT WILL BE PREPARED :
              </Text>
              <Text style={styles.textBlock}>
                ALL THE DOCUMENT SHALL BE PREPARED IN ENGLISH LANGUAGE
              </Text>
            </View>
          </View>
        </View>

        {/* Footer  */}
        <View style={[styles.footer]} fixed>
          {/* Signature */}
          <View style={[styles.flex, { justifyContent: "space-between" }]}>
            <View style={[styles.contentPadding, { alignItems: "center" }]}>
              {settingData?.invoice_digital_signature &&
              salesContractData?.is_digital_signature ? (
                <Image
                  src={settingData.invoice_digital_signature}
                  style={logoSignatureCss}
                />
              ) : (
                <View style={logoSignatureCss}></View>
              )}
              <Text style={styles.textBlock}>SHIPPER / EXPORTER</Text>
            </View>
            <View style={[styles.contentPadding, { alignItems: "center" }]}>
              <View style={logoSignatureCss}></View>
              <Text style={styles.textBlock}>BUYER / IMPORTER</Text>
            </View>
          </View>

          {/* About */}
          <View style={[{ textAlign: "center" }]}>
            <Text style={[styles.boldText]}>
              {stringToUpperCase(settingData?.company_name)}
            </Text>
            <Text style={[styles.textBlock]}>
              {stringToUpperCase(settingData?.company_address)}
            </Text>
            <View style={styles.footerInline}>
              <Text style={[styles.textBlock]}>
                <Text style={styles.boldText}>Email : </Text>
                {stringToUpperCase(settingData?.company_email)}
              </Text>
              <Text style={[styles.textBlock, { marginLeft: 5 }]}>
                <Text style={styles.boldText}>Phone No. : </Text>
                {stringToUpperCase(settingData?.company_phone_1)} /{" "}
                {stringToUpperCase(settingData?.company_phone_2)}
              </Text>
            </View>
            <View style={styles.footerInline}>
              <Text style={[styles.textBlock]}>
                <Text style={styles.boldText}>Subject : </Text>
                Subject
              </Text>
              <Text style={[styles.textBlock, { marginLeft: 5 }]}>
                <Text style={styles.boldText}>GSTIN No. : </Text>
                {stringToUpperCase(settingData?.company_gst_no)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SalesContractInvoice;
